import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLayoutEffect } from 'react';
import { lockScroll, unlockScroll } from '@/utils/scrollLock';
import { 
  Heart,
  Zap,
  Utensils, 
  Droplets, 
  Home, 
  Radio, 
  Car,
  Info,
  Shield,
  Clock,
  ChevronRight,
  BookOpen,
  CheckCircle2,
  ArrowLeft,
} from 'lucide-react';
import { useGamificationStore, POINT_VALUES, type PrepArea } from '@/stores/gamification-store';
import { useKnowledgeStore } from '@/stores/knowledge-store';
import { useAuthStore } from '@/stores/auth-store';
import { useFamilyStore } from '@/stores/family-store';
import PremiumRequiredModal from '@/components/PremiumRequiredModal';
import ArticleModal from '@/components/knowledge/ArticleModal';
import OfficialSourcesNotice from '@/components/OfficialSourcesNotice';
import { getAllAreas } from '@/lib/knowledge-loader';
import { getNativePlatform } from '@/utils/native-platform';

// Hämta kunskapsdata från JSON/Markdown
const knowledgeAreas = getAllAreas();

// Ikoner och färger för varje område i prioritetsordning
const areaConfig = {
  introduction: { icon: BookOpen, color: '#6baaff' },
  water: { icon: Droplets, color: '#6bc5ff' },
  food: { icon: Utensils, color: '#ffcc6b' },
  shelter: { icon: Home, color: '#b86bff' },
  health: { icon: Heart, color: '#ff6b6b' },
  communication: { icon: Radio, color: '#6bffea' },
  information: { icon: Info, color: '#ff6bb3' },
  energy: { icon: Zap, color: '#ffa06b' },
  safety: { icon: Shield, color: '#6bffb3' },
  transport: { icon: Car, color: '#6b8aff' },
};

type AreaKey = keyof typeof areaConfig;

// Komponenter
function AreaCard({ 
  areaKey, 
  onClick,
  userReadArticles,
}: { 
  areaKey: AreaKey; 
  onClick: () => void;
  userReadArticles: Array<{ articleId: string; areaKey: string; readAt: string; pointsAwarded: number }>;
}) {
  const config = areaConfig[areaKey];
  const content = knowledgeAreas[areaKey];
  const Icon = config.icon;
  const { getReadArticlesForArea } = useKnowledgeStore();
  
  const totalArticles = content.articles.length;
  const readArticles = getReadArticlesForArea(areaKey, userReadArticles).length;

  return (
    <button
      onClick={onClick}
      className="group text-left rounded-xl border border-border bg-card p-5 transition-all hover:border-primary/50 hover:shadow-lg"
    >
      <div className="flex items-start gap-4">
        <div 
          className="rounded-lg p-3"
          style={{ backgroundColor: `${config.color}20` }}
        >
          <Icon className="h-6 w-6" style={{ color: config.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-xl group-hover:text-primary transition-colors">
            {content.title}
          </h3>
          <p className="mt-1 text-base text-muted-foreground line-clamp-2">
            {content.description}
          </p>
          <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
            <BookOpen className="h-4 w-4" />
            <span>{readArticles}/{totalArticles} lästa</span>
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
      </div>
    </button>
  );
}

function ArticleCard({ 
  article, 
  areaKey,
  isRead,
  onClick 
}: { 
  article: typeof knowledgeAreas.health.articles[0];
  areaKey: AreaKey;
  isRead: boolean;
  onClick: () => void;
}) {
  // areaKey används för framtida färgkodning
  void areaKey;
  
  const levelLabels = {
    '7d': '7 dagar',
    '30d': '30 dagar', 
    '90d': '90 dagar+',
  };

  const levelColors = {
    '7d': 'bg-green-500/20 text-green-400',
    '30d': 'bg-yellow-500/20 text-yellow-400',
    '90d': 'bg-red-500/20 text-red-400',
  };

  return (
    <button
      onClick={onClick}
      className={`group text-left w-full rounded-lg border bg-card p-4 transition-all hover:border-primary/50 hover:shadow-md ${
        isRead ? 'border-green-500/30 bg-green-500/5' : 'border-border'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="text-base font-medium group-hover:text-primary transition-colors">
              {article.title}
            </h4>
            {isRead && (
              <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
            )}
          </div>
          <p className="mt-1 text-base text-muted-foreground line-clamp-2">
            {article.summary}
          </p>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-3 text-xs">
        <span className={`px-2 py-0.5 rounded-full ${levelColors[article.level]}`}>
          {levelLabels[article.level]}
        </span>
        <span className="flex items-center gap-1 text-muted-foreground">
          <Clock className="h-3 w-3" />
          {article.readTime} min
        </span>
        {isRead && (
          <span className="text-green-500 font-medium">✓ Läst</span>
        )}
      </div>
    </button>
  );
}

function AreaDetailModal({ 
  areaKey, 
  onClose,
  onArticleClick,
  isArticleRead,
  onHome,
}: { 
  areaKey: AreaKey; 
  onClose: () => void;
  onArticleClick: (articleId: string, areaKey: AreaKey) => void;
  isArticleRead: (articleId: string) => boolean;
  onHome?: () => void;
}) {
  const config = areaConfig[areaKey];
  const content = knowledgeAreas[areaKey];
  const Icon = config.icon;
  const nativePlatform = getNativePlatform();
  const isNativeIOS = nativePlatform === 'ios';
  
  const readCount = content.articles.filter(a => isArticleRead(a.id)).length;

  // Lås body scroll när modalen är öppen
  useLayoutEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousDocOverflow = document.documentElement.style.overflow;

    if (isNativeIOS) {
      try {
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
      } catch (_e) {
        void _e;
      }
    } else {
      try { lockScroll(); } catch (_e) { try { document.body.style.overflow = 'hidden'; } catch { /* noop */ } }
    }
    
    // Prevent touch scrolling on background (especially for mobile)
    const preventTouch = (e: TouchEvent) => {
      // Allow scrolling inside the modal, but prevent it on the background
      const target = e.target as HTMLElement;
      if (!target.closest('.knowledge-area-modal')) {
        e.preventDefault();
      }
    };
    
    if (!isNativeIOS) {
      document.addEventListener('touchmove', preventTouch, { passive: false });
    }
    
    return () => {
      if (isNativeIOS) {
        try {
          document.body.style.overflow = previousBodyOverflow;
          document.documentElement.style.overflow = previousDocOverflow;
        } catch (_e) {
          void _e;
        }
      } else {
        try { unlockScroll(); } catch (_e) { try { document.body.style.overflow = ''; document.documentElement.style.overflow = ''; } catch { /* noop */ } }
        document.removeEventListener('touchmove', preventTouch);
      }
    };
  }, [isNativeIOS]);

  return createPortal(
    <div className="knowledge-area-modal fixed z-50 bg-background overflow-y-auto" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
      {/* Header - sticky */}
      <div className="sticky top-0 z-10 flex items-center gap-4 border-b border-border bg-card p-4">
        <button 
          onClick={onClose}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="hidden sm:inline">Tillbaka</span>
        </button>
        {onHome && (
          <button
            onClick={onHome}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            title="Hem till kunskap"
          >
            <Home className="h-5 w-5" />
            <span className="hidden sm:inline">Hem</span>
          </button>
        )}
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Områdesheader */}
        <div className="flex items-center gap-3">
          <div 
            className="rounded-lg p-3"
            style={{ backgroundColor: `${config.color}20` }}
          >
            <Icon className="h-6 w-6" style={{ color: config.color }} />
          </div>
          <div>
            <h1 className="text-3xl font-bold">{content.title}</h1>
            <p className="text-muted-foreground text-base">{content.description}</p>
          </div>
        </div>

        {/* Nyckelkunskaper */}
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-primary" />
            Nyckelkunskaper
          </h3>
          <ul className="space-y-2">
            {content.keyPoints.map((point, i) => (
              <li key={i} className="flex items-start gap-2 text-base">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: config.color }} />
                {point}
              </li>
            ))}
          </ul>
        </div>

        {/* Artiklar */}
        <div>
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            Artiklar
            <span className="text-sm font-normal text-muted-foreground">
              ({readCount}/{content.articles.length} lästa)
            </span>
          </h3>
          <div className="space-y-3">
            {content.articles.map((article) => (
              <ArticleCard 
                key={article.id} 
                article={article} 
                areaKey={areaKey}
                isRead={isArticleRead(article.id)}
                onClick={() => onArticleClick(article.id, areaKey)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}


export default function KnowledgePage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { addPoints } = useGamificationStore();
  const { isArticleRead: isArticleReadFn, markArticleAsRead, unmarkArticleAsRead } = useKnowledgeStore();
  const { user } = useAuthStore();
  const family = useFamilyStore((s) => s.family);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [premiumFeature, setPremiumFeature] = useState<string>('');
  
  // Premium access check
  const hasPremiumAccess = user?.subscriptionTier === 'premium' || !!family;

  const areas = Object.keys(areaConfig) as AreaKey[];
  
  // Wrapper för isArticleRead som använder user.readArticles
  const readArticles = user?.readArticles || [];
  const isArticleRead = (articleId: string) => isArticleReadFn(articleId, readArticles);
  
  // Parse URL parameters to determine which modals should be open
  const params = new URLSearchParams(location.search);
  const selectedArea = params.get('area') as AreaKey | null;
  const selectedArticleId = params.get('article');
  const selectedArticle = selectedArea && selectedArticleId 
    ? { id: selectedArticleId, areaKey: selectedArea }
    : null;
  
  // Hitta nästa artikel i ordning
  const getNextArticle = (currentArticleId: string, currentAreaKey: AreaKey): { id: string; areaKey: AreaKey } | { type: 'area'; areaKey: AreaKey } | null => {
    const currentArea = knowledgeAreas[currentAreaKey];
    const currentIndex = currentArea.articles.findIndex(a => a.id === currentArticleId);
    
    // Om det finns fler artiklar i samma område
    if (currentIndex < currentArea.articles.length - 1) {
      return {
        id: currentArea.articles[currentIndex + 1].id,
        areaKey: currentAreaKey,
      };
    }
    
    // Om vi är på sista artikeln i detta område, gå till nästa område
    const currentAreaIndex = areas.indexOf(currentAreaKey);
    if (currentAreaIndex < areas.length - 1) {
      const nextAreaKey = areas[currentAreaIndex + 1];
      return {
        type: 'area' as const,
        areaKey: nextAreaKey,
      };
    }
    
    // Om vi är på sista artikeln totalt, returnera null
    return null;
  };

  // Check if navigation came from recommendations with scroll position
  const navigationState = location.state as { from?: string; scrollY?: number } | null;
  const cameFromDashboard = navigationState?.from === '/app';

  // Handle closing modals - navigate back using browser history
  const handleCloseArea = () => {
    navigate(-1);
  };

  const handleCloseArticle = () => {
    navigate(-1);
  };

  const handleHome = () => {
    // Navigera till kunskap huvudsida utan query params
    navigate('/app/knowledge', { replace: true });
  };

  // Handle opening area modal - push new URL state
  const handleOpenArea = (areaKey: AreaKey) => {
    const newParams = new URLSearchParams();
    newParams.set('area', areaKey);
    navigate(`?${newParams.toString()}`, { replace: false });
  };

  // Hantera URL query parameters från rekommendationer (only on initial load)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const areaParam = params.get('area') as AreaKey | null;

    // Only process if we came from dashboard with query params
    if (cameFromDashboard && areaParam && areaConfig[areaParam]) {
      // The URL params are already set, no need to modify them
      // Component will render based on URL params
      return;
    }
  }, [cameFromDashboard, location.search]);

  // Handle browser back button - React Router handles this automatically via URL changes

  // Mappa area till gamification PrepArea
  const mapAreaToGamification = (area: AreaKey): PrepArea | 'general' => {
    const mapping: Record<AreaKey, PrepArea | 'general'> = {
      introduction: 'general',
      health: 'health',
      energy: 'energy',
      food: 'food',
      shelter: 'shelter',
      communication: 'communication',
      transport: 'transport',
      water: 'water',
      information: 'information',
      safety: 'safety',
    };
    return mapping[area];
  };

  const handleArticleClick = (articleId: string, areaKey: AreaKey) => {
    // Find the article to check its level
    const article = knowledgeAreas[areaKey].articles.find(a => a.id === articleId);
    
    // Premium required for 30d and 90d articles
    if (article && (article.level === '30d' || article.level === '90d')) {
      if (!hasPremiumAccess) {
        const levelLabel = article.level === '30d' ? '30 dagar' : '90 dagar+';
        setPremiumFeature(`Artiklar märkta med ${levelLabel}`);
        setShowPremiumModal(true);
        return;
      }
    }
    
    // Navigate to article using URL params
    const newParams = new URLSearchParams();
    newParams.set('area', areaKey);
    newParams.set('article', articleId);
    navigate(`?${newParams.toString()}`, { replace: false });
  };

  const handleMarkAsRead = async () => {
    if (!selectedArticle) return;
    
    const { id: articleId, areaKey } = selectedArticle;
    const alreadyRead = isArticleRead(articleId);
    
    try {
      if (alreadyRead) {
        // Avmarkera artikel
        await unmarkArticleAsRead(articleId);
        
        // Refresh user data to get updated readArticles
        const { fetchUser } = useAuthStore.getState();
        await fetchUser();
      } else {
        // Markera artikel som läst
        const gamificationArea = mapAreaToGamification(areaKey);
        const articleTitle = knowledgeAreas[areaKey].articles.find(a => a.id === articleId)?.title || 'artikel';
        
        // Spara i knowledge store
        await markArticleAsRead(articleId, areaKey, POINT_VALUES.READ_ARTICLE);
        
        // Ge poäng endast om det inte är introduction (som mappar till 'general')
        if (gamificationArea !== 'general') {
          addPoints('knowledge', gamificationArea as PrepArea, POINT_VALUES.READ_ARTICLE, `Läste: ${articleTitle}`);
        }
        
        // Refresh user data to get updated readArticles
        const { fetchUser } = useAuthStore.getState();
        await fetchUser();
      }
    } catch (error) {
      console.error('Failed to toggle article read status:', error);
    }
  };
  
  const handleNextArticle = () => {
    if (!selectedArticle) return;
    const next = getNextArticle(selectedArticle.id, selectedArticle.areaKey);
    if (next) {
      if ('type' in next && next.type === 'area') {
        // Gå till nästa område
        handleOpenArea(next.areaKey);
      } else if ('id' in next) {
        // Gå till nästa artikel
        handleArticleClick(next.id, next.areaKey);
      }
    }
  };

  // Hitta artikel om en är vald
  const currentArticle = selectedArticle 
    ? knowledgeAreas[selectedArticle.areaKey].articles.find(a => a.id === selectedArticle.id)
    : null;

  const totalArticles = areas.reduce((sum, areaKey) => sum + knowledgeAreas[areaKey].articles.length, 0);
  const readCount = readArticles.length;
  const articleProgress = totalArticles > 0 ? (readCount / totalArticles) * 100 : 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center"><BookOpen className="h-6 w-6 text-primary mr-3 shrink-0" />{t('knowledge.title')}</h1>
        <p className="mt-2 text-muted-foreground">
          Kunskap och övning är nyckeln till en fungerande beredskap. Här kan du lära dig mer om hemberedskap och fördjupa dig inom olika beredskapsområden.
        </p>

        {/* Progress: lästa artiklar */}
        <div className="mt-4 rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Lästa artiklar</span>
            <span className="text-sm text-muted-foreground">{readCount} / {totalArticles}</span>
          </div>
          <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary to-primary/80 transition-all duration-500 ease-out"
              style={{ width: `${Math.min(articleProgress, 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Hemberedskap */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Hemberedskap</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <AreaCard 
            key="introduction" 
            areaKey="introduction" 
            onClick={() => handleOpenArea('introduction')}
            userReadArticles={readArticles}
          />
        </div>
      </div>

      {/* Beredskapsområden */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Beredskapsområden</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {areas.filter(area => area !== 'introduction').map((area) => (
            <AreaCard 
              key={area} 
              areaKey={area} 
              onClick={() => handleOpenArea(area)}
              userReadArticles={readArticles}
            />
          ))}
        </div>
      </div>

      <OfficialSourcesNotice
        title="Officiella källor och viktig information"
        description="Kunskapsinnehållet i Beredskapshubben är ett privat stödmaterial baserat på officiella svenska källor. Appen är inte en myndighetstjänst. Kontrollera alltid aktuella råd och rekommendationer hos ansvariga myndigheter."
      />

      {/* Modal för område */}
      {selectedArea && !selectedArticle && (
        <AreaDetailModal 
          areaKey={selectedArea}
          onClose={handleCloseArea}
          onArticleClick={handleArticleClick}
          isArticleRead={isArticleRead}
          onHome={handleHome}
        />
      )}

      {/* Modal för artikel */}
      {selectedArticle && currentArticle && (
        <ArticleModal
          article={currentArticle}
          areaKey={selectedArticle.areaKey}
          isRead={isArticleRead(selectedArticle.id)}
          onClose={handleCloseArticle}
          onMarkAsRead={handleMarkAsRead}
          onNextArticle={getNextArticle(selectedArticle.id, selectedArticle.areaKey) ? handleNextArticle : undefined}
          onHome={handleHome}
        />
      )}
      
      {/* Premium Required Modal */}
      <PremiumRequiredModal
        open={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        feature={premiumFeature}
      />
    </div>
  );
}
